import type { VoltStatusMap } from "../ui/voltStatus";
import { countEntries, formatDateTime } from "./attendanceLogic";
import type {
  AttendanceClosing,
  AttendanceEntry,
  AttendanceSession,
  AttendanceSessionKind,
} from "./types";

export const CLOSING_STATUS_MAP: VoltStatusMap = {
  open: { tone: "neutral", label: "In Arbeit" },
  review: { tone: "warning", label: "Wartet auf Freigabe" },
  approved: { tone: "info", label: "Freigegeben" },
  drafted: { tone: "info", label: "Entwurf an die BV" },
  sent: { tone: "success", label: "Gesendet" },
};

export const closingFileName = (path: string) => path.split("/").pop() ?? path;

export type ClosingList = {
  kind: AttendanceSessionKind;
  path: string;
  file: string;
  sessions: number;
  people: number;
};

type ListEntry = Pick<AttendanceEntry, "session_id" | "status" | "invited">;

/** One entry per PDF, big list first; people counts only rows that appear in the PDF. */
export const closingLists = (
  closing: Pick<AttendanceClosing, "pdf_big_path" | "pdf_small_path">,
  sessions: Pick<AttendanceSession, "id" | "kind">[],
  entries: ListEntry[],
): ClosingList[] =>
  (["big", "small"] as const).flatMap((kind) => {
    const path = kind === "big" ? closing.pdf_big_path : closing.pdf_small_path;
    if (!path) return [];
    const ids = new Set(
      sessions.filter((s) => s.kind === kind).map((s) => s.id),
    );
    const listed = entries.filter((e) => ids.has(e.session_id));
    return [
      {
        kind,
        path,
        file: closingFileName(path),
        sessions: ids.size,
        people: countEntries(listed).invited,
      },
    ];
  });

export const listCountText = (l: Pick<ClosingList, "sessions" | "people">) =>
  l.sessions > 1
    ? `${l.sessions} Sitzungen · ${l.people} Einträge`
    : `${l.people} ${l.people === 1 ? "Person" : "Personen"}`;

export type ClosingStep = { label: string; sub: string; done: boolean };

const APPROVED = new Set(["approved", "drafted", "sent"]);

export const closingSteps = (input: {
  closing: AttendanceClosing;
  open: number;
  signatoryName: string | null;
  signatoryFunction: string;
  live: boolean;
}): ClosingStep[] => {
  const { closing: c, open } = input;
  const listsDone = !!(c.pdf_big_path || c.pdf_small_path);
  const to = input.live
    ? input.signatoryName
      ? `an ${input.signatoryName}`
      : "an den Unterzeichner"
    : "an die Testadresse";
  const mailed = c.review_notified_at
    ? `${formatDateTime(c.review_notified_at)} ${to}` +
      (c.reminded_at ? ` · Erinnerung ${formatDateTime(c.reminded_at)}` : "")
    : "noch nicht verschickt";
  const approved = APPROVED.has(c.status);
  return [
    {
      label: "Sitzungen da",
      sub: `${c.session_ids.length} ${c.session_ids.length === 1 ? "Sitzung" : "Sitzungen"} aus dem Kalender`,
      done: c.session_ids.length > 0,
    },
    {
      label: "Listen fertig",
      sub: !listsDone
        ? "werden erstellt"
        : open
          ? `${open} ${open === 1 ? "Status steht" : "Status stehen"} noch auf offen`
          : "alle Status geklärt",
      done: listsDone,
    },
    {
      label: `Review-Mail an ${input.signatoryFunction}`,
      sub: mailed,
      done: !!c.review_notified_at,
    },
    {
      label: "Freigabe und Versand",
      sub: approved
        ? [
            c.approved_by_name,
            c.approved_at ? formatDateTime(c.approved_at) : null,
            `Version ${c.version}`,
          ]
            .filter(Boolean)
            .join(", ")
        : `wartet auf ${input.signatoryFunction}`,
      done: approved,
    },
  ];
};
